import { useState, useEffect } from "react";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { ScrollArea } from "../../ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { Checkbox } from "../../ui/checkbox";
import {
  Search,
  Star,
  Loader2,
  ChevronDown,
  ChevronRight,
  GraduationCap,
  Lightbulb,
  Plus,
  X,
} from "lucide-react";
import {
  searchSkills,
  getSkillsDomains,
  getSkillsRepositories,
} from "../../../lib/api";

export function SkillSelector({ selectedSkills, onSkillsChange, matiere, niveau, suggestedSkills = [] }) {
  const [search, setSearch] = useState("");
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(false);
  const [domains, setDomains] = useState([]);
  const [selectedDomain, setSelectedDomain] = useState("");
  const [expandedCategories, setExpandedCategories] = useState({});
  const [showSuggestions, setShowSuggestions] = useState(true);
  const [repositories, setRepositories] = useState([]);
  const [selectedRepo, setSelectedRepo] = useState("");

  useEffect(() => {
    fetchRepositories();
  }, []);

  useEffect(() => {
    if (selectedRepo) {
      fetchDomains();
      fetchSkills();
    }
  }, [matiere, niveau, selectedDomain, search, selectedRepo]);

  const fetchRepositories = async () => {
    try {
      const { data } = await getSkillsRepositories();
      setRepositories(data);
      if (data.length > 0 && !selectedRepo) {
        setSelectedRepo(data[0].id);
      }
    } catch (err) {
      console.error("Error fetching repositories:", err);
    }
  };

  const fetchDomains = async () => {
    if (!selectedRepo) return;
    try {
      const { data } = await getSkillsDomains({ repository_id: selectedRepo });
      setDomains(data);
    } catch (err) {
      console.error("Error fetching domains:", err);
    }
  };

  const fetchSkills = async () => {
    if (!selectedRepo) return;
    setLoading(true);
    try {
      const params = {
        repository_id: selectedRepo,
        limit: 50,
      };
      if (selectedDomain && selectedDomain !== "all") params.domain = selectedDomain;
      if (search) params.query = search;

      const { data } = await searchSkills(params);
      setSkills(data);
    } catch (err) {
      console.error("Error fetching skills:", err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const isSkillSelected = (skillId) => {
    return selectedSkills.some((s) => s.skill_id === skillId);
  };

  const toggleSkill = (skill) => {
    if (isSkillSelected(skill.id)) {
      onSkillsChange(selectedSkills.filter((s) => s.skill_id !== skill.id));
    } else {
      const isFirst = selectedSkills.length === 0;
      onSkillsChange([
        ...selectedSkills,
        {
          skill_id: skill.id,
          label: skill.label,
          is_main: isFirst,
          custom_label: null,
        },
      ]);
    }
  };

  const setMainSkill = (skillId) => {
    onSkillsChange(
      selectedSkills.map((s) => ({
        ...s,
        is_main: s.skill_id === skillId,
      }))
    );
  };

  // Group skills by category
  const groupedSkills = skills.reduce((acc, skill) => {
    const cat = skill.category || "Autre";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(skill);
    return acc;
  }, {});

  return (
    <div className="space-y-4">
      {/* Selected skills */}
      {selectedSkills.length > 0 && (
        <div className="space-y-2">
          <label className="text-xs font-medium text-slate-500">Compétences sélectionnées</label>
          <div className="flex flex-wrap gap-2">
            {selectedSkills.map((skill) => (
              <Badge
                key={skill.skill_id}
                variant={skill.is_main ? "default" : "secondary"}
                className="flex items-center gap-1 cursor-pointer"
                onClick={() => setMainSkill(skill.skill_id)}
              >
                {skill.is_main && <Star className="w-3 h-3" />}
                <span className="max-w-[200px] truncate">{skill.label}</span>
                <X
                  className="w-3 h-3 ml-1 hover:text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSkillsChange(selectedSkills.filter((s) => s.skill_id !== skill.skill_id));
                  }}
                />
              </Badge>
            ))}
          </div>
          <p className="text-xs text-slate-400">
            Cliquez sur une competence pour la definir comme principale
          </p>
        </div>
      )}

      {/* Suggested skills from linked resource */}
      {suggestedSkills.length > 0 && showSuggestions && (
        <div className="space-y-2 p-3 bg-amber-50 rounded-lg border border-amber-200">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-amber-700 flex items-center gap-1">
              <Lightbulb className="w-3 h-3" />
              Suggestions basees sur la ressource liee
            </label>
            <button
              onClick={() => setShowSuggestions(false)}
              className="text-xs text-amber-600 hover:text-amber-800"
            >
              Masquer
            </button>
          </div>
          <div className="flex flex-wrap gap-1">
            {suggestedSkills.filter(s => !isSkillSelected(s.skill_id)).slice(0, 5).map((suggestion) => (
              <Badge
                key={suggestion.skill_id}
                variant="outline"
                className="cursor-pointer hover:bg-amber-100 border-amber-300 text-amber-700"
                onClick={() => {
                  const isFirst = selectedSkills.length === 0;
                  onSkillsChange([
                    ...selectedSkills,
                    {
                      skill_id: suggestion.skill_id,
                      label: suggestion.label,
                      is_main: isFirst,
                      custom_label: null,
                    },
                  ]);
                }}
              >
                <Plus className="w-3 h-3 mr-1" />
                <span className="max-w-[180px] truncate">{suggestion.label}</span>
              </Badge>
            ))}
          </div>
          {suggestedSkills.filter(s => !isSkillSelected(s.skill_id)).length === 0 && (
            <p className="text-xs text-amber-600">Toutes les suggestions ont ete ajoutees</p>
          )}
        </div>
      )}

      {/* Search and filters */}
      <div className="space-y-2">
        {/* Repository selector */}
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-slate-500 whitespace-nowrap">Referentiel :</label>
          <Select value={selectedRepo} onValueChange={setSelectedRepo}>
            <SelectTrigger className="flex-1 h-9">
              <SelectValue placeholder="Choisir un referentiel" />
            </SelectTrigger>
            <SelectContent>
              {repositories.map((r) => (
                <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Search and domain filter */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher une competence..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 h-9"
            />
          </div>
          <Select value={selectedDomain} onValueChange={setSelectedDomain}>
            <SelectTrigger className="w-[180px] h-9">
              <SelectValue placeholder="Domaine" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les domaines</SelectItem>
              {domains.map((d) => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Skills list */}
      <ScrollArea className="h-[250px] border rounded-lg p-2">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : Object.keys(groupedSkills).length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-slate-400">
            <GraduationCap className="w-8 h-8 mb-2" />
            <p>Aucune competence trouvee</p>
          </div>
        ) : (
          <div className="space-y-2">
            {Object.entries(groupedSkills).map(([category, categorySkills]) => (
              <div key={category} className="border-b last:border-0 pb-2">
                <button
                  onClick={() => toggleCategory(category)}
                  className="flex items-center gap-2 w-full text-left py-1 px-2 hover:bg-slate-50 rounded text-sm font-medium"
                >
                  {expandedCategories[category] ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                  <span>{category}</span>
                  <Badge variant="outline" className="ml-auto text-xs">
                    {categorySkills.length}
                  </Badge>
                </button>
                {expandedCategories[category] && (
                  <div className="ml-6 mt-1 space-y-1">
                    {categorySkills.map((skill) => (
                      <div
                        key={skill.id}
                        className={`flex items-start gap-2 p-2 rounded cursor-pointer transition-colors ${
                          isSkillSelected(skill.id)
                            ? "bg-indigo-50 border border-indigo-200"
                            : "hover:bg-slate-50"
                        }`}
                        onClick={() => toggleSkill(skill)}
                      >
                        <Checkbox
                          checked={isSkillSelected(skill.id)}
                          className="mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm">{skill.label}</p>
                          {skill.description && (
                            <p className="text-xs text-slate-500 mt-0.5">{skill.description}</p>
                          )}
                          <div className="flex gap-1 mt-1">
                            {skill.niveaux?.map((n) => (
                              <Badge key={n} variant="outline" className="text-[10px] px-1">
                                {n.toUpperCase()}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </ScrollArea>
    </div>
  );
}

export default SkillSelector;
